import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { messaggioDi } from "@/lib/messaggio-errore";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

/**
 * La pagina a cui Supabase manda la persona quando Claude chiede di collegarsi (c37, D3 e D10).
 *
 * L'ordine conta: prima il codice di sicurezza (senza, non si legge nemmeno la richiesta), poi il
 * controllo del server (il codice dato, e che chi chiede sia davvero Claude: la registrazione dei
 * client è aperta a chiunque), poi i due bottoni. «Consenti» approva solo dopo che il server ha
 * scritto il collegamento. Le chiamate di consenso sono quelle di auth-js 2.118, fatte a mano:
 * aggiornare supabase-js tocca le sessioni di tutto il gestionale (D11).
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
// Dove può tornare la persona: gli stessi redirect che il server accetta (MCP_REDIRECT_AMMESSI).
const REDIRECT_CLAUDE = [
  "https://claude.ai/api/mcp/auth_callback",
  "https://claude.com/api/mcp/auth_callback",
];

export const redirectDiClaude = (url) => {
  try {
    const u = new URL(url);
    return REDIRECT_CLAUDE.includes(u.origin + u.pathname);
  } catch {
    return false;
  }
};

async function decidi(authorizationId, azione) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const r = await fetch(`${SUPABASE_URL}/auth/v1/oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`, {
    method: "POST",
    headers: { apikey: ANON, Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ action: azione }),
  });
  const dati = await r.json().catch(() => ({}));
  if (!r.ok || !dati.redirect_url) throw new Error("Non sono riuscito a rispondere a Claude. Riparti da Claude e riprova.");
  if (!redirectDiClaude(dati.redirect_url)) throw new Error("L'indirizzo di ritorno non è quello di Claude: mi fermo.");
  return dati.redirect_url;
}

const PUO = [
  "Leggere agenda, clienti e preventivi fermi, e i numeri del mese se sei il titolare",
  "Aggiungere un cliente o una nota alla scheda",
  "Preparare bozze di preventivo, che ritrovi nel gestionale",
  "Lasciarti proposte per migliorare il gestionale",
];
const NON_PUO = [
  "Inviare preventivi, firmare o cambiare gli stati",
  "Cancellare qualcosa, o toccare soldi, WhatsApp e calendario",
  "Vedere telefoni, email, indirizzi, costi o margini",
];
const NOTE = "Le note scritte a mano le legge, ma senza telefoni ed email.";

function Elenco({ titolo, voci, Icona, classeIcona, testId }) {
  return (
    <div data-testid={testId}>
      <h2 className="text-sm font-semibold text-foreground">{titolo}</h2>
      <ul className="mt-inside space-y-inside">
        {voci.map((v) => (
          <li key={v} className="flex items-start gap-inside text-sm text-muted-foreground">
            <Icona className={`mt-0.5 h-4 w-4 shrink-0 ${classeIcona}`} aria-hidden />
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Scheda({ children, testId }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-card sm:p-8" data-testid={testId}>
        <div className="mb-card flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-subtle">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Collegare Claude al gestionale</h1>
          </div>
        </div>
        {children}
      </Card>
    </div>
  );
}

export default function ConsensoClaudePage() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id");
  const { aal } = useAuth();
  const [fase, setFase] = useState("controllo"); // controllo · pronto · invio · errore
  const [dettagli, setDettagli] = useState(null);
  const [errore, setErrore] = useState("");
  const partita = useRef(false);
  const collegato = useRef(false); // il server ha già scritto il collegamento (e chiuso quello di prima)

  const conCodice = aal?.currentLevel === "aal2";

  useEffect(() => {
    // Prima il codice: senza, non si legge la richiesta. Un solo giro, anche se la pagina si ridisegna.
    if (!authorizationId || !conCodice || partita.current) return;
    partita.current = true;
    api
      .post("/mcp/collegamenti/controllo", { authorization_id: authorizationId })
      .then((r) => {
        setDettagli(r.data);
        setFase("pronto");
      })
      .catch((e) => {
        setErrore(messaggioDi(e));
        setFase("errore");
      });
  }, [authorizationId, conCodice]);

  const rispondi = async (azione) => {
    setFase("invio");
    try {
      // Il collegamento si scrive solo al «Consenti», e prima di approvare: chi rifiuta o chiude la pagina non lascia niente.
      if (azione === "approve") {
        await api.post("/mcp/collegamenti", { authorization_id: authorizationId });
        collegato.current = true;
      }
      window.location.assign(await decidi(authorizationId, azione));
    } catch (e) {
      const perche = e.response ? messaggioDi(e) : e.message;
      // Se l'errore arriva dopo che il server ha scritto il collegamento, il Claude di prima è già chiuso: lo si dice.
      setErrore(collegato.current ? `${perche} Il collegamento di prima è chiuso: riparti da Claude e ricollega.` : perche);
      setFase("errore");
    }
  };

  if (!authorizationId) {
    return (
      <Scheda testId="consenso-senza-richiesta">
        <p className="text-sm text-muted-foreground">
          Questa pagina si apre da Claude, quando aggiungi il gestionale come connettore. Aprila da lì.
        </p>
      </Scheda>
    );
  }

  if (!conCodice) {
    return (
      <Scheda testId="consenso-serve-il-codice">
        <p className="text-sm text-muted-foreground">
          Per collegare Claude serve il codice di verifica, lo stesso con cui entri nel gestionale. Se non l&#39;hai ancora
          attivato, fallo da «Il tuo accesso» e poi riparti da Claude.
        </p>
        <div className="mt-card">
          <Button asChild variant="outline">
            <Link to="/sicurezza" data-testid="consenso-vai-a-sicurezza">
              Attiva il codice di verifica
            </Link>
          </Button>
        </div>
      </Scheda>
    );
  }

  if (fase === "errore") {
    return (
      <Scheda testId="consenso-errore">
        <p role="alert" className="text-sm text-foreground" data-testid="consenso-errore-testo">
          {errore}
        </p>
        <p className="mt-inside text-sm text-muted-foreground">Puoi chiudere questa pagina e ripartire da Claude.</p>
      </Scheda>
    );
  }

  if (fase === "controllo") {
    return (
      <Scheda testId="consenso-controllo">
        <p role="status" className="text-sm text-muted-foreground">
          Controllo la richiesta…
        </p>
      </Scheda>
    );
  }

  const nome = dettagli?.client_nome || "Claude";
  return (
    <Scheda testId="consenso-pronto">
      <p className="text-sm text-foreground">
        <strong>{nome}</strong> chiede di lavorare nel gestionale con i tuoi permessi, non di più. Dopo l&#39;ok torni a
        Claude su <span data-testid="consenso-host">{dettagli?.redirect_host}</span>. Consenti solo se l&#39;hai appena chiesto tu,
        aggiungendo il gestionale in Claude: se qualcuno ti ha mandato questo link, premi «Rifiuta».
      </p>
      <div className="mt-card space-y-card">
        <Elenco titolo="Potrà" voci={PUO} Icona={Check} classeIcona="text-success" testId="consenso-puo" />
        <Elenco titolo="Non potrà mai" voci={NON_PUO} Icona={X} classeIcona="text-danger" testId="consenso-non-puo" />
      </div>
      <p className="mt-card text-sm text-muted-foreground">
        {NOTE} Lo scolleghi quando vuoi da Impostazioni, nella scheda «Claude collegato».
      </p>
      <div className="mt-card flex flex-col-reverse gap-inside sm:flex-row sm:justify-between">
        <Button variant="outline" disabled={fase === "invio"} onClick={() => rispondi("deny")} data-testid="consenso-rifiuta">
          Rifiuta
        </Button>
        <Button disabled={fase === "invio"} onClick={() => rispondi("approve")} data-testid="consenso-consenti">
          {fase === "invio" ? "Un attimo…" : "Consenti"}
        </Button>
      </div>
    </Scheda>
  );
}
